import { useState } from "react";

import {
  Autocomplete,
  AppBar,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Drawer,
  IconButton,
  Container,
  MenuItem,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";

import { Add, Close } from "@mui/icons-material";

import AppLogo from "@/fragments/AppLogo";
import { currentUser } from "@/App";
import AccountManagerCard from "./AccountManagerCard";
import ManagedAccountCard from "./ManagedAccountCard";
import CurrentAccountCard from "./CurrentAccountCard";
import { countryCodes } from "@/Constants";
import { getToken } from "@/utils";
import { useAccounts } from "@/services/hooks";
import { addAccountManager } from "@/services/api";
import { formatE164 } from "@/services/phone";

export default function Accounts() {
  const [open, setOpen] = useState(false);

  const { data, isLoading: loading } = useAccounts();
  const managedAccounts = data?.managed_accounts ?? [];
  const accountManagers = data?.account_managers ?? [];

  const currentAccount = {
    user: currentUser,
    token: getToken(),
  };

  return (
    <Box>
      <PageAppBar />
      <Container maxWidth="sm" disableGutters>
        <Loading loading={loading} />

        {!loading && (
          <Box sx={{ p: 2 }}>
            <CurrentAccountCard account={currentAccount} />

            {managedAccounts.length > 0 && (
              <Card
                sx={{
                  p: 2,
                  my: 4,
                  borderRadius: "10px",
                  backgroundColor: "rgba(36,36,36,.9)",
                }}
              >
                <Typography variant="h6">Managed Accounts</Typography>
                {managedAccounts?.map((account, index) => (
                  <Box key={index}>
                    <ManagedAccountCard account={account} />
                  </Box>
                ))}
              </Card>
            )}

            {currentUser.role == "artist" && (
              <Card
                sx={{
                  p: 2,
                  my: 4,
                  borderRadius: "10px",
                  backgroundColor: "rgba(36,36,36,.9)",
                }}
              >
                <Typography variant="h6">Account Managers</Typography>
                {accountManagers?.map((account, index) => (
                  <Box key={index}>
                    <AccountManagerCard account={account} />
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  onClick={() => setOpen(true)}
                  fullWidth
                  style={{ borderRadius: "25px" }}
                  sx={{ my: 2 }}
                  startIcon={<Add />}
                >
                  Add Account Manager
                </Button>
                <AddAccountManagerDrawer open={open} setOpen={setOpen} />
              </Card>
            )}

            {currentUser.role == "artist" && <></>}
          </Box>
        )}
      </Container>
    </Box>
  );
}

function AddAccountManagerDrawer({ open, setOpen }) {
  const [countryCode, setCountryCode] = useState("+1");
  const [phone, setPhone] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const formattedPhone = () => {
    return formatE164(countryCode, phone).e164;
  };

  const handleAddAccount = async () => {
    setLoading(true);
    await addAccountManager(formattedPhone());
    window.location.reload();
  };

  const validLength = () => {
    return formatE164(countryCode, phone).isValid;
  };

  return (
    <Box>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          style: { borderRadius: "20px 20px 0 0" },
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ px: 2, py: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Add Account Manager
            </Typography>
            <Box style={{ display: "flex", justifyContent: "space-between" }}>
              <Autocomplete
                id="country-code"
                value={countryCode}
                onChange={(e, newValue) => setCountryCode(newValue)}
                options={countryCodes}
                disableClearable
                sx={{ mr: 1 }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
              <TextField
                label="Phone Number"
                variant="outlined"
                type="telephone"
                value={phone}
                onChange={(e) =>
                  setPhone(e.target.value.replace(/[^0-9]/g, ""))
                }
                fullWidth
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                // submit on enter
                onKeyDown={(e) => {
                  if (e.key === "Enter" && validLength()) {
                    handleAddAccount();
                  }
                }}
              />
            </Box>
            <Box
              style={{
                display: "flex",
                alignItems: "flex-start",
                width: "100%",
              }}
              sx={{ my: 4 }}
            >
              <Checkbox
                checked={confirm}
                onChange={(e) => setConfirm(e.target.checked)}
                sx={{ pt: 0.5, pr: 2 }}
              />
              <Typography variant="body1" color="darkgray">
                I understand this user will be able to log in as me and perform
                all actions on my behalf.
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleAddAccount}
              disabled={!validLength() || !confirm || loading}
              startIcon={<Add />}
              sx={{ my: 2 }}
              style={{ borderRadius: "25px" }}
            >
              Add Account Manager
            </Button>
          </Box>
        </Container>
      </Drawer>
    </Box>
  );
}

function PageAppBar() {
  return (
    <AppBar
      position="sticky"
      elevation={0}
      style={{ background: "rgba(19,19,19,.9)" }}
    >
      <Toolbar style={{ display: "flex", justifyContent: "space-between" }}>
        <IconButton onClick={() => (window.location.href = "/")}>
          <Close />
        </IconButton>
        <AppLogo />
        <Box style={{ width: "40px" }} />
      </Toolbar>
    </AppBar>
  );
}

function Loading({ loading }) {
  return (
    <>
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "60vh",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
}
